/**
 * EditProjectModal - Modal for editing an existing project
 */

import { X } from 'lucide-react';
import type { ProjectListItem } from '../../lib/projects-api';
import { Dropdown } from '../common';
import { ProjectStatuses } from '@resolve-ai/shared';

interface EditProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  project: ProjectListItem | null;
  form: {
    name: string;
    description: string;
    status: typeof ProjectStatuses[keyof typeof ProjectStatuses];
  };
  onFormChange: (form: {
    name: string;
    description: string;
    status: typeof ProjectStatuses[keyof typeof ProjectStatuses];
  }) => void;
  isSaving: boolean;
}

export function EditProjectModal ({
  isOpen,
  onClose,
  onUpdate,
  project,
  form,
  onFormChange,
  isSaving,
}: EditProjectModalProps) {
  if (!isOpen || !project) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">Edit Project</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Project Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => onFormChange({ ...form, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => onFormChange({ ...form, description: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
              rows={3}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <Dropdown
              options={[
                { label: 'Active', value: ProjectStatuses.ACTIVE },
                { label: 'Inactive', value: ProjectStatuses.INACTIVE },
                { label: 'Archived', value: ProjectStatuses.ARCHIVED },
              ]}
              value={form.status}
              onChange={(val) => onFormChange({ ...form, status: val as typeof ProjectStatuses[keyof typeof ProjectStatuses] })}
            />
          </div>
          <div className="flex items-center gap-3 pt-4">
            <button
              onClick={onUpdate}
              disabled={isSaving}
              className={`flex-1 px-4 py-2 rounded-xl font-medium transition-all ${!isSaving
                  ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                  : 'bg-gray-400 text-gray-200 cursor-not-allowed'
                }`}
            >
              {isSaving ? 'Updating Project...' : 'Update'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600 rounded-xl transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
